/**
 * pairing-client — the SPA's HTTP client for the pairing Worker. Implements the
 * frozen @spaces/contracts api/pairing surface:
 *
 *   POST /pair          — exchange a code (TV setup code OR Remote share key) for
 *                         an access token + meetingJwt + room.
 *   POST /pair/code     — (TV, Bearer) mint a SHORT_LIVED remote share key.
 *   POST /token/refresh — (TV) exchange a refresh token for a fresh access token.
 *
 * Default-deny posture: the access token is carried on every authenticated call;
 * no secrets live in client code — the only credential is the pairing code the
 * user types, which the pairing Worker exchanges for tokens.
 */

import type {
    MintPairingCodeRequest,
    MintPairingCodeResponse,
    PairRequest,
    PairResponse,
    RefreshTokenRequest,
    RefreshTokenResponse
} from '@spaces/contracts';

/** A failed pairing call, carrying the HTTP status + the server's error code. */
export class PairingError extends Error {
    readonly status: number;
    readonly code: string;

    constructor(status: number, code: string, message: string) {
        super(message);
        this.name = 'PairingError';
        this.status = status;
        this.code = code;
    }
}

async function post<T>(url: string, body: unknown, token?: string): Promise<T> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };

    if (token) {
        headers.authorization = `Bearer ${token}`;
    }

    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
    const text = await res.text();
    let parsed: unknown;

    try {
        parsed = text ? JSON.parse(text) : undefined;
    } catch {
        parsed = undefined;
    }

    if (!res.ok) {
        const err = (parsed as { error?: { code?: string; message?: string } } | undefined)?.error;

        throw new PairingError(res.status, err?.code ?? 'error', err?.message ?? `HTTP ${res.status}`);
    }

    return parsed as T;
}

/** Client for the pairing Worker, bound to its base URL. */
export class PairingClient {
    private readonly baseUrl: string;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl.replace(/\/+$/, '');
    }

    /** Exchange a pairing code (setup code or share key) for tokens + room. */
    pair(pairingCode: string, deviceId: string): Promise<PairResponse> {
        const body: PairRequest = { pairingCode: pairingCode.trim(), deviceId };

        return post<PairResponse>(`${this.baseUrl}/pair`, body);
    }

    /** TV: mint a SHORT_LIVED remote share key (Bearer access token). */
    mintShareKey(accessToken: string): Promise<MintPairingCodeResponse> {
        const body: MintPairingCodeRequest = { kind: 'SHORT_LIVED' };

        return post<MintPairingCodeResponse>(`${this.baseUrl}/pair/code`, body, accessToken);
    }

    /** TV: exchange a refresh token for a fresh access token. */
    refresh(refreshToken: string): Promise<RefreshTokenResponse> {
        const body: RefreshTokenRequest = { refreshToken };

        return post<RefreshTokenResponse>(`${this.baseUrl}/token/refresh`, body);
    }
}
